import { ErrorBoundary } from '@sentry/react';
import { clsx } from 'clsx';
import { Suspense, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import LoadingScreen from '~/components/LoadingScreen';
import MembershipRedirectChecker from '~/components/MembershipRedirectChecker';
import { ErrorBoundaryFallback } from '~/components/layout/ErrorBoundaryFallback';
import { useF24OcrReadyNotification } from '~/hooks/f24/useF24OcrReadyNotification';
import { group, identify } from '~/hooks/segment';
import { useActiveCompanyId } from '~/hooks/useActiveCompany';
import { useCompany } from '~/hooks/useCompany';
import useEnsureSocketConnection from '~/hooks/useEnsureSocketConnection';
import { SubmenuContentContainer } from '~/layouts/dashboard/navigation/NavigationSidebarItem';
import NavigationSubmenu from '~/layouts/dashboard/navigation/NavigationSubmenu';
import { useCurrentSubscription } from '~/services/subscription';
import { useLoggedUser } from '~/services/user';
import { useActiveSubmenuContent, useIsAnySubmenuOpen } from '~/state/navigationSidebar';
import styles from './index.module.scss';
import NavigationSidebar from './navigation/NavigationSidebar';
import useIsNavExcluded from './useIsNavExcluded';
import { DashboardAlerts } from '../DashboardAlerts';

const SubmenuPanel: React.FC = () => {
  const isAnySubmenuOpen = useIsAnySubmenuOpen();
  const activeContent = useActiveSubmenuContent();

  return (
    <div className={clsx(styles.submenuPanel, { [styles.submenuPanelOpen!]: isAnySubmenuOpen })}>
      <div className={styles.submenuPanelContent}>
        {activeContent && (
          <SubmenuContentContainer title={activeContent.title}>
            <NavigationSubmenu sections={activeContent.sections} />
          </SubmenuContentContainer>
        )}
      </div>
    </div>
  );
};

const DashboardLayout: React.FC<{ isCompanyAccessible?: boolean }> = ({ isCompanyAccessible = true }) => {
  useEnsureSocketConnection();
  useF24OcrReadyNotification();

  const companyId = useActiveCompanyId();
  const { data: user } = useLoggedUser();
  const { data: company } = useCompany(companyId);

  const { data: subscription } = useCurrentSubscription();
  const isNavExcluded = useIsNavExcluded();

  useEffect(() => {
    identify(user);
  }, [user]);

  useEffect(() => {
    if (company && subscription) group(company, subscription);
  }, [company, subscription]);

  return (
    <div className={styles.root}>
      <MembershipRedirectChecker />
      {!isNavExcluded && <NavigationSidebar isCompanyAccessible={isCompanyAccessible} />}
      {!isNavExcluded && <SubmenuPanel />}

      <div className={styles.dashboardPage}>
        <Suspense fallback={<LoadingScreen />}>
          <ErrorBoundary fallback={ErrorBoundaryFallback}>
            <DashboardAlerts />
            <div className={styles.content}>
              <Outlet />
            </div>
          </ErrorBoundary>
        </Suspense>
      </div>
    </div>
  );
};

export default DashboardLayout;
